import { useCallback, useMemo, useState } from 'react';
import { getConfig } from '../../../config';
import {
  TrendingVideoClient,
  type CreateTrendingVideoPayload,
  type ListTrendingVideosResponse,
  type TrendingVideo,
  type UpdateTrendingVideoPayload,
} from '../../../infrastructure/TrendingVideoClient';

interface UploadState {
  name: string;
  progress: number;
  error?: string;
}

export const MAX_VIDEO_SIZE_BYTES = 3 * 1024 * 1024;
export const MAX_VISIBLE_VIDEOS = 20;
export const ALLOWED_VIDEO_TYPES = ['video/mp4'];

interface UseTrendingVideo {
  uploadingFiles: Record<string, UploadState>;
  uploadFile: (file: File) => Promise<{ videoUrl: string } | null>;
  clearUploadingFile: (fileName: string) => void;
  create: (payload: CreateTrendingVideoPayload) => Promise<TrendingVideo>;
  update: (videoId: string, patch: UpdateTrendingVideoPayload) => Promise<TrendingVideo>;
  remove: (videoId: string) => Promise<void>;
  list: (params?: {
    visibleOnly?: boolean;
    limit?: number;
    lastEvaluatedKey?: string;
  }) => Promise<ListTrendingVideosResponse>;
  updateOrders: (videos: Array<{ videoId: string; order: number }>) => Promise<void>;
}

export const useTrendingVideo = (): UseTrendingVideo => {
  const config = getConfig();
  const client = useMemo(() => new TrendingVideoClient(config.apiUrl, config.apiKey), [config.apiUrl, config.apiKey]);
  const [uploadingFiles, setUploadingFiles] = useState<Record<string, UploadState>>({});

  const setUploadState = useCallback((name: string, patch: Partial<UploadState>) => {
    setUploadingFiles((prev) => ({
      ...prev,
      [name]: { name, progress: prev[name]?.progress ?? 0, ...prev[name], ...patch },
    }));
  }, []);

  const validate = (file: File): string | null => {
    if (!ALLOWED_VIDEO_TYPES.includes(file.type)) {
      return `File type ${file.type || 'unknown'} is not supported. Please upload an mp4 video.`;
    }
    if (file.size > MAX_VIDEO_SIZE_BYTES) {
      return `File size ${(file.size / 1024 / 1024).toFixed(2)}MB exceeds the ${MAX_VIDEO_SIZE_BYTES / 1024 / 1024}MB limit.`;
    }
    return null;
  };

  const uploadFile = useCallback(
    async (file: File): Promise<{ videoUrl: string } | null> => {
      const validationError = validate(file);
      if (validationError) {
        setUploadState(file.name, { progress: 0, error: validationError });
        return null;
      }

      setUploadingFiles((prev) => ({ ...prev, [file.name]: { name: file.name, progress: 0 } }));

      try {
        // Rename the object with a uuid so two merchandisers uploading with the
        // same filename don't overwrite each other.
        const fileExtension = file.name.split('.').pop() || 'mp4';
        const renamedFile = `${crypto.randomUUID()}.${fileExtension}`;

        const { presignedUrl, cdnUrl } = await client.getPresignedUrl(renamedFile, file.type, file.size);
        setUploadState(file.name, { progress: 25 });

        const putResponse = await fetch(presignedUrl, {
          method: 'PUT',
          headers: { 'Content-Type': file.type },
          body: file,
        });

        if (!putResponse.ok) {
          throw new Error(`Upload failed with status ${putResponse.status}`);
        }

        setUploadState(file.name, { progress: 100 });
        return { videoUrl: cdnUrl };
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Upload failed';
        setUploadState(file.name, { error: message });
        return null;
      }
    },
    [client, setUploadState],
  );

  const clearUploadingFile = useCallback((fileName: string) => {
    setUploadingFiles((prev) => {
      const next = { ...prev };
      delete next[fileName];
      return next;
    });
  }, []);

  const create = useCallback((payload: CreateTrendingVideoPayload) => client.create(payload), [client]);
  const update = useCallback(
    (videoId: string, patch: UpdateTrendingVideoPayload) => client.update(videoId, patch),
    [client],
  );
  const remove = useCallback((videoId: string) => client.delete(videoId), [client]);
  const list = useCallback(
    (params?: { visibleOnly?: boolean; limit?: number; lastEvaluatedKey?: string }) => client.list(params),
    [client],
  );
  const updateOrders = useCallback(
    (videos: Array<{ videoId: string; order: number }>) => client.updateOrders(videos),
    [client],
  );

  return {
    uploadingFiles,
    uploadFile,
    clearUploadingFile,
    create,
    update,
    remove,
    list,
    updateOrders,
  };
};
